import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "../api";
import { MEASUREMENT_FIELDS, type Measurement } from "../types";
import { useTelegramBackButton } from "../hooks/useTelegramBackButton";
import { tg } from "../telegram";
import { ymd, fmtSlot } from "../utils/date";
import { useT } from "../i18n";

type TypeFilter = "" | "plan" | "emergency" | "missed" | "late" | "offline";

export default function Measurements() {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const today = useMemo(() => ymd(new Date()), []);
  const sevenAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return ymd(d);
  }, []);
  const [from, setFrom] = useState<string>(sevenAgo);
  const [to, setTo] = useState<string>(today);
  const [type, setType] = useState<TypeFilter>("");
  const [items, setItems] = useState<Measurement[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Measurement | null>(null);

  useEffect(() => {
    setError(null);
    setItems(null);
    const q = new URLSearchParams({ from, to });
    if (type) q.set("type", type);
    api.get<Measurement[]>(`/api/measurements?${q.toString()}`)
      .then(setItems)
      .catch((e: ApiError) => setError(e.message));
  }, [from, to, type]);

  const exportXlsx = () => {
    const q = new URLSearchParams({ from, to, lang });
    api.get<Blob>(`/api/export?${q.toString()}`)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `energo-${from}_to_${to}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((e: ApiError) => setError(e.message));
  };

  const typeOptions: { v: TypeFilter; label: string }[] = [
    { v: "", label: t("filter.all") },
    { v: "plan", label: t("type.plan") },
    { v: "emergency", label: t("type.emergency") },
    { v: "offline", label: t("type.offline") },
    { v: "missed", label: t("type.missed") },
    { v: "late", label: t("type.late") },
  ];

  return (
    <div>
      <h1>{t("records.title")}</h1>

      <div className="card">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
          <label>
            <div style={{ fontSize: 12, color: "var(--hint)" }}>{t("records.from")}</div>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
          </label>
          <label>
            <div style={{ fontSize: 12, color: "var(--hint)" }}>{t("records.to")}</div>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} />
          </label>
        </div>
        <div className="period-tabs">
          {typeOptions.map(o => (
            <button key={o.v}
              className={"period-tab" + (o.v === type ? " active" : "")}
              onClick={() => setType(o.v)}>{o.label}</button>
          ))}
        </div>
        <div className="records-actions">
          <button className="btn excel" onClick={exportXlsx}>
            {t("records.download_xlsx")}
          </button>
          <button className="btn preview" onClick={() => {
            tg()?.HapticFeedback?.impactOccurred("light");
            navigate(`/records/custom?from=${from}&to=${to}`);
          }}>
            {t("records.custom_preview")}
          </button>
        </div>
        <div style={{ fontSize: 11, color: "var(--hint)", marginTop: 8 }}>
          {t("records.xlsx_per_day")}
        </div>
      </div>

      {error && <div className="alert error">{error}</div>}
      {!items && !error && <div className="empty-state"><span className="spinner" /></div>}
      {items && items.length === 0 && (
        <div className="empty-state">{t("common.no_data")}</div>
      )}

      {items && items.length > 0 && (
        <div className="scroll-x">
          <table className="table">
            <thead>
              <tr>
                <th>{t("records.col_slot")}</th>
                <th>{t("records.col_who")}</th>
                <th>{t("records.col_type")}</th>
                <th>{t("records.col_status")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map(m => {
                // Priority: emergency > offline > missed > late
                const cls = m.is_emergency ? "emergency"
                          : m.is_offline ? "offline"
                          : m.status === "missed" ? "missed"
                          : m.is_late ? "late" : "";
                const icon = m.is_emergency ? "🚨"
                          : m.is_offline ? "🟠"
                          : m.status === "missed" ? "🚫"
                          : m.is_late ? "🟨" : "📋";
                return (
                  <tr key={m.id} className={cls}>
                    <td>{fmtSlot(m.slot_time)}</td>
                    <td style={{ maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {m.user_full_name ?? "—"}
                    </td>
                    <td>{icon}</td>
                    <td>{m.status === "completed" ? t("status.completed") : m.status === "missed" ? t("status.miss_short") : m.status}</td>
                    <td><button className="period-tab" onClick={() => setSelected(m)}>›</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && <MeasurementDetail m={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function MeasurementDetail({ m, onClose }: { m: Measurement; onClose: () => void }) {
  const { t } = useT();
  useTelegramBackButton(onClose, [onClose]);
  useEffect(() => { tg()?.HapticFeedback?.impactOccurred("light"); }, []);
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200,
      display: "flex", alignItems: "flex-end",
    }} onClick={onClose}>
      <div style={{
        background: "var(--bg)", width: "100%", maxHeight: "85vh", overflowY: "auto",
        borderRadius: "16px 16px 0 0", padding: "16px",
      }} onClick={e => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>
          {t("records.detail.title", { id: m.id })} {m.is_emergency ? "🚨" : m.status === "missed" ? "🚫" : ""}
        </h2>
        <div style={{ fontSize: 13, color: "var(--hint)", marginBottom: 12 }}>
          {t("records.detail.slot")}: {fmtSlot(m.slot_time)}<br/>
          {m.user_full_name && <>{t("records.detail.responsible")}: <b>{m.user_full_name}</b><br/></>}
          {m.started_at && <>{t("records.detail.started")}: {fmtSlot(m.started_at)}<br/></>}
          {m.completed_at && <>{t("records.detail.completed")}: {fmtSlot(m.completed_at)}<br/></>}
          {m.response_time_sec !== null && <>{t("records.detail.response")}: {m.response_time_sec} {t("records.detail.seconds")}<br/></>}
          {m.fill_duration_sec !== null && <>{t("records.detail.fill")}: {m.fill_duration_sec} {t("records.detail.seconds")}<br/></>}
          {m.is_emergency && m.emergency_reason && <>{t("records.detail.reason")}: <i>{m.emergency_reason}</i><br/></>}
        </div>
        <table className="table">
          <tbody>
            {MEASUREMENT_FIELDS.map(f => {
              const v = (m as unknown as Record<string, unknown>)[f.key];
              return (
                <tr key={f.key}>
                  <td style={{ width: "60%" }}>{f.label}{f.unit ? ` (${f.unit})` : ""}</td>
                  <td style={{ fontFamily: "ui-monospace, monospace" }}>{v == null ? "—" : String(v)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <button className="btn" style={{ marginTop: 12 }} onClick={onClose}>{t("common.close")}</button>
      </div>
    </div>
  );
}
